import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { apiDelete, apiGet, apiPost } from "../../shared/apiClient";
import { getApiBaseUrl } from "../../config/appSettings";
import { useToast } from "../../shared/ToastContext";

const inputClass =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20";

function resolveMediaUrl(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
  const base = getApiBaseUrl();
  return `${base}${raw.startsWith("/") ? raw : `/${raw}`}`;
}

function getValue(row, key) {
  if (!row || !key) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, key)) return row[key];
  const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
  return row[key] ?? row[pascalKey];
}

function truncate(value, max = 90) {
  const text = String(value ?? "").trim();
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

export function ProductsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";

  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(0);
  const [cloningId, setCloningId] = useState(0);

  const effectiveCustomerId = isAdmin
    ? selectedCustomerId
      ? Number(selectedCustomerId)
      : null
    : Number(user?.customerId || 0) || null;

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const loadCustomers = async () => {
      const res = await apiGet("/api/Customers");
      if (cancelled || !res.ok) return;
      const list = Array.isArray(res.data) ? res.data : [];
      setCustomers(list);
      setSelectedCustomerId((prev) => prev || (list[0]?.id ? String(list[0].id) : ""));
    };
    void loadCustomers();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const loadLookups = useCallback(async (customerId) => {
    if (!customerId) {
      setCategories([]);
      return;
    }
    const res = await apiGet(`/api/ProductCategories?customerId=${encodeURIComponent(customerId)}`);
    setCategories(res.ok && Array.isArray(res.data) ? res.data : []);
  }, []);

  useEffect(() => {
    void loadLookups(effectiveCustomerId);
  }, [effectiveCustomerId, loadLookups]);

  const load = useCallback(async () => {
    setLoading(true);
    const query = new URLSearchParams();
    if (search.trim()) query.set("search", search.trim());
    if (effectiveCustomerId) query.set("customerId", String(effectiveCustomerId));
    if (categoryFilter) query.set("categoryId", categoryFilter);
    const path = query.toString() ? `/api/Products?${query}` : "/api/Products";
    const res = await apiGet(path);
    setLoading(false);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to load products"), "error");
      return;
    }
    setRows(Array.isArray(res.data) ? res.data : []);
  }, [effectiveCustomerId, search, categoryFilter, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const cloneRow = async (row) => {
    const confirmed = window.confirmDialog
      ? await window.confirmDialog(`Clone "${row.name || "this product"}"? A new copy will be created.`)
      : window.confirm(`Clone "${row.name || "this product"}"? A new copy will be created.`);
    if (!confirmed) return;

    setCloningId(row.id);
    const res = await apiPost(`/api/Products/${row.id}/clone`, {});
    setCloningId(0);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to clone product"), "error");
      return;
    }

    const newId = Number(res.data?.id ?? res.data?.Id ?? 0);
    toast.push("Product cloned", "success");
    if (newId) {
      navigate(`/products/${newId}/edit`);
      return;
    }
    await load();
  };

  const removeRow = async (row) => {
    const confirmed = window.confirmDialog
      ? await window.confirmDialog("Delete this product?")
      : window.confirm("Delete this product?");
    if (!confirmed) return;
    setDeletingId(row.id);
    const res = await apiDelete(`/api/Products/${row.id}`);
    setDeletingId(0);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to delete product"), "error");
      return;
    }
    toast.push("Product deleted", "success");
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Products</h1>
            <p className="mt-1 text-sm text-slate-500">
              Manage catalog details, choose HTML or JSON detail pages, and preview before publish.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/products/new")}
            className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Add New
          </button>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-3">
          <input
            className={inputClass}
            placeholder="Search name, code, barcode"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {isAdmin && (
            <select
              className={inputClass}
              value={selectedCustomerId}
              onChange={(e) => {
                setSelectedCustomerId(e.target.value);
                setCategoryFilter("");
              }}
            >
              {customers.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name || c.code || `Customer ${c.id}`}
                </option>
              ))}
            </select>
          )}
          <select className={inputClass} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name || c.code || `Category ${c.id}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1500px] text-left text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Product</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Code / Barcode</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Category / Brand</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Pricing</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Detail method</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => {
                  const src = resolveMediaUrl(getValue(row, "image"));
                  const galleryCount = Array.isArray(row.images)
                    ? row.images.length
                    : Array.isArray(row.Images)
                      ? row.Images.length
                      : 0;
                  const mode = getValue(row, "detailContentMode") === "json" ? "json" : "html";
                  const subtitle = truncate(getValue(row, "subtitle"), 70);
                  const shortDescription = truncate(getValue(row, "shortDescription"), 110);
                  const unit = getValue(row, "unitName");
                  const minQty = getValue(row, "minQuantity");
                  const discount = getValue(row, "discountPrice");
                  const price = getValue(row, "price");

                  return (
                    <tr key={row.id} className="hover:bg-slate-50/80">
                      <td className="px-4 py-3">
                        <div className="flex min-w-[280px] gap-3">
                          {src ? (
                            <img
                              src={src}
                              alt=""
                              className="h-14 w-14 shrink-0 rounded-lg border border-slate-200 object-cover"
                            />
                          ) : (
                            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-dashed border-slate-200 text-[10px] text-slate-400">
                              No img
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900">{getValue(row, "name") || "—"}</div>
                            {subtitle ? <div className="mt-0.5 text-xs text-slate-500">{subtitle}</div> : null}
                            {shortDescription ? (
                              <div className="mt-1 text-xs leading-5 text-slate-500">{shortDescription}</div>
                            ) : null}
                            {galleryCount > 0 ? (
                              <div className="mt-1 text-[11px] text-slate-400">{galleryCount} gallery images</div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div>{getValue(row, "code") || "—"}</div>
                        <div className="mt-1 text-xs text-slate-500">{getValue(row, "barcode") || "No barcode"}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div>{getValue(row, "categoryName") || "—"}</div>
                        <div className="mt-1 text-xs text-slate-500">{getValue(row, "brandName") || "No brand"}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {discount != null && discount !== "" ? (
                          <div>
                            <span className="font-medium">{discount}</span>{" "}
                            <span className="text-xs text-slate-400 line-through">{price}</span>
                          </div>
                        ) : (
                          <div className="font-medium">{price ?? "—"}</div>
                        )}
                        <div className="mt-1 text-xs text-slate-500">
                          {unit ? `${unit}` : "No unit"}
                          {minQty != null ? ` · min ${minQty}` : ""}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                            mode === "json"
                              ? "bg-violet-50 text-violet-700 ring-1 ring-violet-200"
                              : "bg-sky-50 text-sky-700 ring-1 ring-sky-200"
                          }`}
                        >
                          {mode === "json" ? "JSON" : "HTML"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => navigate(`/products/${row.id}/preview`)}
                            className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-800 hover:bg-brand-100"
                          >
                            Preview
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate(`/products/${row.id}/edit`)}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => void cloneRow(row)}
                            disabled={cloningId === row.id}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                          >
                            {cloningId === row.id ? "Cloning..." : "Clone"}
                          </button>
                          <button
                            type="button"
                            onClick={() => void removeRow(row)}
                            disabled={deletingId === row.id}
                            className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
                          >
                            {deletingId === row.id ? "Deleting..." : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-500">
                      No products found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
